// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/MetaAdsCard.tsx (ADAPT)
// ADAPTED (plan phase 4, step 3.1): TP's `Funnels` rows (name, score badge, ordered steps) as Scout's own card, driven
// by the funnel analysis. TP's ad share and "lands on" are left out: nothing joins ads to funnels yet (TD-4.12). The
// score is FA's CRO scorecard and may be withheld ("Not scored yet" on the primary funnel; a later funnel FA did not evaluate
// shows no badge at all, as FA only grades the primary one). The title says "Capturing" only while FA works. Step boxes are TP's neutral placeholders: no
// image comes from anywhere but our own data (P4-6), and the funnel carries none.
import { Bot, ChevronRight, ExternalLink } from 'lucide-react';

import { Badge } from '@/components/ui/shadcn/badge';
import { Card } from '@/components/ui/shadcn/card';
import type { Funnel, FunnelStep, FunnelSummary } from '@/contracts/onboarding-session';
import { safeHttpUrl } from '@/lib/safeHttpUrl';
import { cn } from '@/lib/utils';

import { FUNNEL_CARD } from '../copy';
import type { DataState } from '../state/buildTranscript';
import { JobBadge, type JobBadgeStatus } from './JobBadge';
import { StepStatusIcon } from './StepStatusIcon';

const ENTRANCE = 'animate-in fade-in duration-300 motion-reduce:animate-none';
/** TP's thresholds for the score badge's tone. */
const GOOD_SCORE = 70;
const OK_SCORE = 50;

/** What the card draws: a card while running or with funnels; one line when unavailable (or the read failed) or no funnel was found. */
export type FunnelView = 'running' | 'ready' | 'unavailable' | 'none';

export function funnelView(funnel: DataState<FunnelSummary>): FunnelView {
  if (funnel.state === 'idle' || funnel.state === 'loading') return 'running';
  if (funnel.state === 'error') return 'unavailable';
  if (funnel.data.state === 'running') return 'running';
  if (funnel.data.state === 'unavailable') return 'unavailable';
  return funnel.data.funnels.length === 0 ? 'none' : 'ready';
}

const BADGE: Record<'running' | 'ready', JobBadgeStatus> = { running: 'running', ready: 'done' };

/** FA's status for a funnel it did not grade on purpose (it only evaluates the primary one). */
const NOT_EVALUATED = 'not_evaluated';

function ScoreBadge({ funnel, primary }: { funnel: Funnel; primary: boolean }) {
  const { value, grade, status } = funnel.score;
  if (value === null) {
    // A later funnel FA did not evaluate has nothing to say: "not scored yet" would promise a score that is not coming.
    if (!primary && status === NOT_EVALUATED) return null;
    return (
      <Badge intent="neutral" data-testid="funnel-score" className="ml-auto shrink-0">
        {FUNNEL_CARD.notScored}
      </Badge>
    );
  }
  return (
    <Badge
      intent={value >= GOOD_SCORE ? 'success' : value >= OK_SCORE ? 'warning' : 'error'}
      data-testid="funnel-score"
      className="ml-auto shrink-0"
    >
      <span className="sr-only">{FUNNEL_CARD.scoreWord}</span>
      {grade ? `${value} · ${grade}` : String(value)}
    </Badge>
  );
}

function StepLabel({ step }: { step: FunnelStep }) {
  const href = safeHttpUrl(step.url);
  const text = <span className={cn('text-caption', step.captured ? 'text-ds-text-2' : 'text-ds-text-3')}>{step.label}</span>;
  return (
    <span className="flex flex-col items-center gap-0.5">
      <span
        aria-hidden
        className={cn('h-8 w-6 rounded-xs border border-ds-hairline', step.captured ? 'bg-ds-paper-2' : 'border-dashed bg-transparent')}
      />
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
        >
          {text}
          <ExternalLink className="size-3 shrink-0 text-ds-text-3" aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      ) : (
        text
      )}
      {step.captured ? null : <span className="sr-only">{FUNNEL_CARD.notCaptured}</span>}
    </span>
  );
}

/** How much of the funnel was captured: FA's completeness over all its steps when it gave one, else the drawn steps' count. */
function capturedLine(funnel: Funnel): string {
  if (funnel.stepsCompleteness !== null) return FUNNEL_CARD.stepsCompleteness(funnel.stepsCompleteness);
  return FUNNEL_CARD.stepsCaptured(funnel.steps.filter((s) => s.captured).length, funnel.steps.length);
}

function FunnelRow({ funnel, index }: { funnel: Funnel; index: number }) {
  return (
    <li
      data-testid="funnel-row"
      className={cn('flex flex-col gap-2 rounded-md border border-ds-hairline p-3', ENTRANCE, 'slide-in-from-bottom-1')}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center gap-2">
        <span data-testid="funnel-name" className="min-w-0 truncate text-tab text-foreground">{funnel.name}</span>
        <ScoreBadge funnel={funnel} primary={index === 0} />
      </div>
      {funnel.steps.length > 0 ? (
        <>
          <ol className="flex flex-wrap items-center gap-1" aria-label={FUNNEL_CARD.stepsLabel(funnel.name)}>
            {funnel.steps.map((step, i) => (
              <li key={`${i}:${step.label}`} data-testid="funnel-step" data-captured={step.captured} className="flex items-center gap-1">
                {i > 0 ? <ChevronRight aria-hidden className="size-3.5 text-ds-text-3" /> : null}
                <StepLabel step={step} />
              </li>
            ))}
          </ol>
          <span className="text-label text-ds-text-2">{capturedLine(funnel)}</span>
        </>
      ) : null}
    </li>
  );
}

export interface FunnelCardProps {
  funnel: DataState<FunnelSummary>;
  /** The brand's name, for the title ("Capturing Truekind's funnels"). */
  brand?: string | null;
  /** The site the funnels were captured from, for the source line. */
  site?: string;
}

/**
 * Scout's funnel card: the funnels FA captured on the site, the primary one first (the BFF's order), each with its
 * score and ordered steps. While FA works it is a spinner row; when FA failed (or the read did) it is one line and
 * no card; when FA found no funnel it is one line too.
 */
export function FunnelCard({ funnel, brand, site }: FunnelCardProps) {
  const view = funnelView(funnel);
  if (view === 'unavailable' || view === 'none') {
    return (
      <p data-testid="funnel-line" className={cn('text-body-sm text-ds-text-2', ENTRANCE)}>
        {view === 'unavailable' ? FUNNEL_CARD.unavailable : FUNNEL_CARD.none}
      </p>
    );
  }
  const funnels = funnel.state === 'ready' && funnel.data.state === 'ready' ? funnel.data.funnels : [];
  const who = brand?.trim() || site;
  return (
    <Card role="region" aria-label="Funnels" data-testid="funnel-card" data-state={view} className="w-full overflow-hidden">
      <header className="flex items-center gap-2.5 border-b border-ds-hairline px-3 py-2.5">
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2">
          <Bot className="size-3.5" />
        </span>
        <h3 className="min-w-0 flex-1 truncate text-heading-sm text-foreground">{view === 'ready' ? FUNNEL_CARD.readyTitle(who) : FUNNEL_CARD.title(who)}</h3>
        <JobBadge status={BADGE[view]} data-testid="funnel-badge" />
      </header>
      <div className="flex flex-col gap-2 px-3 py-3">
        <p className="text-label text-ds-text-3">{FUNNEL_CARD.source(site)}</p>
        {view === 'running' ? (
          <div className={cn('flex items-center gap-2.5', ENTRANCE, 'slide-in-from-bottom-1')} data-status="running">
            <StepStatusIcon status="running" />
            <span className="onb-shimmer min-w-0 flex-1 truncate text-body-sm">{FUNNEL_CARD.running}</span>
            <span className="sr-only">(in progress)</span>
          </div>
        ) : (
          <ul aria-label={FUNNEL_CARD.listLabel} className="flex flex-col gap-2">
            {funnels.map((f, i) => (
              <FunnelRow key={f.id} funnel={f} index={i} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
